import 'vanilla-cookieconsent';
import i18n from './i18n';

window.setCookie = (cname, cvalue, exdays) => {
    const d = new Date();

    d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);

    const expires = 'expires=' + d.toUTCString();
    document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
};

window.getCookie = (cookieName) => {
    const cookie = {};

    document.cookie.split(';').forEach((el) => {
        const [key, value] = el.split('=');

        cookie[key.trim()] = value;
    });

    return cookie[cookieName];
};

window.deleteCookie = (name) => {
    document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
};

// Check if customer is our contact
const params = new URLSearchParams(window.location.search);
const isContact = params.has('uid');

// Check if cookies are allowed
window.cookiesDeclined = window.getCookie('cc_declined') === 'true';

// Get customer's timezone
const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Revision is a value that determines if the popup should reappear.
// When revision changes and differs from the value set in the consent cookie the popup will appear.
// - Cookies are enabled by default on first load
// - If customer declines the cookie banner won't show for 24 hours. The choice is saved.
//   After that time the banner will reappear.
// - If customer accepts the banner will disappear for good.
//   Consent cookie will expire after 6 months by default.
const revision = window.getCookie('cc_cookie')
    ? JSON.parse(window.getCookie('cc_cookie')).categories.length === 1 && !window.cookiesDeclined
        ? new Date().getUTCMilliseconds()
        : JSON.parse(window.getCookie('cc_cookie')).revision
    : 0;

// List of GDPR compliant countries
const showCookieBanner = () => {
    const GDPR_COMPLIANT_COUNTRIES = [
        'Europe/Vienna',
        'Europe/Brussels',
        'Europe/Sofia',
        'Europe/Zagreb',
        'Asia/Famagusta',
        'Asia/Nicosia',
        'Europe/Prague',
        'Europe/Copenhagen',
        'Europe/Tallinn',
        'Europe/Helsinki',
        'Europe/Paris',
        'Europe/Berlin',
        'Europe/Busingen',
        'Europe/Athens',
        'Europe/Budapest',
        'Atlantic/Reykjavik',
        'Europe/Dublin',
        'Europe/Rome',
        'Europe/Riga',
        'Europe/Vaduz',
        'Europe/Vilnius',
        'Europe/Luxembourg',
        'Europe/Malta',
        'Europe/Amsterdam',
        'Europe/Oslo',
        'Europe/Warsaw',
        'Atlantic/Azores',
        'Atlantic/Madeira',
        'Europe/Lisbon',
        'Europe/Bucharest',
        'Europe/Bratislava',
        'Europe/Ljubljana',
        'Africa/Ceuta',
        'Atlantic/Canary',
        'Europe/Madrid',
        'Europe/Stockholm',
        'Europe/London',
        'America/Los_Angeles',
    ];
    return GDPR_COMPLIANT_COUNTRIES.includes(tz);
};

const initCookieBanner = () => {
    const settings = {
        current_lang: 'en',
        mode: 'opt-out',
        autoclear_cookies: true,
        page_scripts: true,
        revision: revision,
        gui_options: {
            consent_modal: {
                layout: 'cloud',
                position: 'bottom left',
                transition: 'slide',
                swap_buttons: true,
            },
        },
        languages: {
            en: {
                consent_modal: {
                    description: `${i18n.global.t('cookies.description')} <a href="https://app.termly.io/document/cookie-policy/53fd9961-6ae3-43c6-8a50-dd69ebadb2e5" target="_blank">${i18n.global.t('cookies.policy')}</a>`,
                    primary_btn: {
                        text: i18n.global.t('cookies.accept'),
                        role: 'accept_all',
                    },
                    secondary_btn: {
                        text: i18n.global.t('cookies.decline'),
                        role: 'accept_necessary',
                    },
                },
                settings_modal: {
                    title: 'Cookie preferences',
                    save_settings_btn: 'Save settings',
                    accept_all_btn: 'Accept all',
                    reject_all_btn: 'Reject all',
                    close_btn_label: 'Close',
                    cookie_table_headers: [
                        { col1: 'Name' },
                        { col2: 'Domain' },
                        { col3: 'Expiration' },
                        { col4: 'Description' },
                    ],
                    blocks: [
                        {
                            title: 'Strictly necessary cookies',
                            description:
                                'These cookies are essential for the proper functioning of my website. Without these cookies, the website would not work properly.',
                            toggle: {
                                value: 'necessary',
                                enabled: true,
                                readonly: true,
                            },
                        },
                        {
                            title: 'Analytics cookies',
                            description:
                                'These cookies collect information about how you use the website, which pages you visited and which links you clicked on. All of the data is anonymized and cannot be used to identify you.',
                            toggle: {
                                value: 'analytics',
                                enabled:
                                    !window.getCookie('cc_cookie') ||
                                    JSON.parse(window.getCookie('cc_cookie')).categories.includes('analytics')
                                        ? true
                                        : false,
                                readonly: false,
                            },
                        },
                    ],
                },
            },
        },
        onAccept: (cookie) => {
            if (cookie.categories.length === 1) {
                window.ipse.dispatch({
                    action: 'cookies_declined',
                });
                window.setCookie('cc_declined', 'true', 1);
                window.location.reload();
            } else {
                window.ipse.dispatch({
                    action: 'cookies_accepted',
                });
                window.deleteCookie('cc_declined');
            }
        },
    };

    const cc = initCookieConsent();

    cc.run(settings);

    // Hide cookie banner after customer
    // scrolled a specific amount of pixels
    if (!window.cookiesDeclined) {
        const initialScroll = window.scrollY;
        const offset = 2 * window.innerHeight;

        window.addEventListener('scroll', () => {
            if (window.scrollY > initialScroll + offset || window.scrollY < initialScroll - offset) {
                document.documentElement.classList.remove('show--consent');
            }
        });
    }
};

const handleOtherCountries = () => {
    const script_selector = 'data-cookiecategory';
    const scripts = document.querySelectorAll('script[' + script_selector + ']');
    const _createNode = (type) => {
        const el = document.createElement(type);
        return el;
    };

    scripts.forEach((script) => {
        script.type = 'text/javascript';
        script.removeAttribute(script_selector);

        // create fresh script (with the same code)
        const fresh_script = _createNode('script');
        fresh_script.textContent = script.innerHTML;

        // Copy attributes over to the new "revived" script
        (function (destination, source) {
            const attributes = source.attributes;
            const len = attributes.length;

            for (var i = 0; i < len; i++) {
                var attr_name = attributes[i].nodeName;
                destination.setAttribute(attr_name, source[attr_name] || source.getAttribute(attr_name));
            }
        })(fresh_script, script);

        script.parentNode.replaceChild(fresh_script, script);
    });
};

export const initCookies = (loadVue) => {
    // skip cookies for iosview and contacts
    if (window.App.isIosView) {
        document.documentElement.classList.add('iosview');
        loadVue();
        return;
    }
    if (isContact) {
        loadVue();
        return;
    }
    // Initialise CookieConsent instance if cookies are allowed
    if (showCookieBanner() && !window.cookiesDeclined) {
        initCookieBanner();
        loadVue();
    } else {
        if (window.cookiesDeclined) {
            loadVue();
            return;
        }
        // Load analytics scripts for non GDPR countries
        handleOtherCountries();
        loadVue();
    }
};
